import { AgentEditMode, OiqAgentType } from '@logz-pkg/enums';
import {
  formatAgentDisplayTitle,
  getAlertIdFromLogzAgentTitle,
  isAlertRcaAgent,
  isLockedSystemAgent,
  isSystemAgent,
} from '@logz-pkg/utils';

export { formatAgentDisplayTitle, getAlertIdFromLogzAgentTitle, isAlertRcaAgent };

export const isLogzAgent = isSystemAgent;
export const isReadOnlyAgent = isLockedSystemAgent;

type AlertAgentLike = { id: string; title?: string | null; type?: string | null };

export type AlertRcaState = { kind: 'v2'; agentId: string } | { kind: 'v1' } | { kind: 'none' };

// Backend alert-agent title contract: `logz-<name>-<alertId>-<type>-alert-agent` (agent.service.ts
// buildLogzAgentTitle). endsWith is UUID-safe; the leading dash avoids 1-vs-11 prefix collisions.
const matchesAlertAgent = (agent: AlertAgentLike, alertId: string | number, agentType: OiqAgentType): boolean =>
  agent.type === agentType && Boolean(agent.title?.endsWith(`-${alertId}-${agentType.toLowerCase()}-alert-agent`));

export const getAlertRcaState = (agents: ReadonlyArray<AlertAgentLike>, alertId: string | number): AlertRcaState => {
  const v2 = agents.find(agent => matchesAlertAgent(agent, alertId, OiqAgentType.AlertAIAnalysisv2));

  if (v2) return { kind: 'v2', agentId: v2.id };

  if (agents.some(agent => matchesAlertAgent(agent, alertId, OiqAgentType.AlertAIAnalysis))) return { kind: 'v1' };

  return { kind: 'none' };
};

// The OrionIQ standalone is the oiq-artemis `orioniq-ui` mesh service, served at /orioniq.
export const ORIONIQ_BASE_PATH = '/orioniq';

export const AGENT_HUB_AGENTS_PATH = `${ORIONIQ_BASE_PATH}/agents/definitions`;

// Owns the account's monthly AI budget and daily limits — NOT the Agents Hub, which only lists definitions.
export const ORIONIQ_CAPPING_SETTINGS_PATH = `${ORIONIQ_BASE_PATH}/settings?settingGroup=capping`;

export const ORIONIQ_AI_SETTINGS_COPY = {
  title: 'AI settings live in the OrionIQ Platform',
  description:
    'Account-level AI settings beyond Logz.io — usage, limits and budget configuration — are managed in the OrionIQ Platform.',
  ctaLabel: 'Configure AI settings in OrionIQ',
  aiDisabledNote: 'AI based features are disabled on this account. Contact support to enable them.',
} as const;

interface IBuildAgentHubUrlArgs {
  fromNav?: boolean;
  accountId?: number;
}

const withQuery = (path: string, params: URLSearchParams): string => {
  const query = params.toString();

  return query ? `${path}?${query}` : path;
};

const setSwitchToAccountId = (params: URLSearchParams, accountId: number | undefined): void => {
  if (typeof accountId === 'number') params.set('switchToAccountId', String(accountId));
};

// Entry URL into the OrionIQ app from an Open 360 / SIEM surface.
// `fromNav` is load-bearing, not decoration: orioniq-ui's `use-initial-routing.hook` redirects to
// `/agents/chat` and opens it fullscreen when the param is present and `oiqHermesGeneralAgent` is on.
// Omit it to land on the Agents Hub table itself.
export const buildAgentHubUrl = ({ fromNav = false, accountId }: IBuildAgentHubUrlArgs = {}): string => {
  const params = new URLSearchParams();

  if (fromNav) params.set('fromNav', 'true');

  setSwitchToAccountId(params, accountId);

  return withQuery(AGENT_HUB_AGENTS_PATH, params);
};

interface IAgentHubAgentUrlOptions {
  mode?: AgentEditMode;
  accountId?: number;
}

export const getAgentHubAgentUrl = (agentId: string, { mode, accountId }: IAgentHubAgentUrlOptions = {}): string => {
  const params = new URLSearchParams();

  if (mode) params.set('mode', mode);

  setSwitchToAccountId(params, accountId);

  return withQuery(`${AGENT_HUB_AGENTS_PATH}/${encodeURIComponent(agentId)}`, params);
};

export const ORIONIQ_INTEGRATIONS_PATH = `${ORIONIQ_BASE_PATH}/agents/integrations`;

export const getOiqIntegrationsUrl = ({ accountId }: { accountId?: number } = {}): string => {
  const params = new URLSearchParams();

  setSwitchToAccountId(params, accountId);

  return withQuery(ORIONIQ_INTEGRATIONS_PATH, params);
};

export const ORIONIQ_MEMORY_PATH = `${ORIONIQ_BASE_PATH}/agents/memory`;

// Deep-links to a memory document on the OrionIQ standalone app. The URL is NAME-based (not scope-based):
// the Memory route resolves `?doc=<name>` across BOTH account docs and the viewer's user docs (USER.md), so
// this one builder is correct for either scope — the name is what selects the doc.
export const getMemoryDocUrl = (name: string): string => `${ORIONIQ_MEMORY_PATH}?doc=${encodeURIComponent(name)}`;

const AGENT_TYPE_DISPLAY: Record<string, string> = {
  Custom1: 'Custom',
  Custom2: 'Custom +',
  Basic: 'Basic',
  AlertAIAnalysis: 'Alert AI Analysis',
  AlertAIAnalysisv2: 'Alert AI Analysis',
  ScheduledErrorLogsReport: 'Error Logs Analysis',
  AlertsFatigue: 'Alerts Fatigue',
  PostChangeValidator: 'Post Change Validator',
  LogDistributionMonitor: 'Log Distribution Monitor',
  MetricsDistributionMonitor: 'Metrics Distribution Monitor',
  ScheduledErrorMetricsReport: 'Scheduled Error Metrics Analysis',
  Triage: 'Triage',
};

export const getAgentTypeDisplayLabel = (type: string | null | undefined): string =>
  (type && AGENT_TYPE_DISPLAY[type]) || type || 'N/A';

export const getAgentTriggerDisplayLabel = (trigger: string | null | undefined): string =>
  trigger ? trigger.toUpperCase() : 'N/A';
